import { useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classnames from 'classnames';

// components
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import { PdpRespCondensedListedContent } from '../PdpRespCondensedListedContent/PdpRespCondensedListedContent';
import { usePdpRecentHistoryTracker } from '../hooks/usePdpRecentHistoryTracker';
import { PdpSharedBuyerIncentivesModule } from '../shared/PdpSharedBuyerIncentivesModule/PdpSharedBuyerIncentivesModule';
import { PdpRespSimilarItemsGrid } from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpSharedSellerItemsCarousel } from '../shared/PdpSharedSellerItemsCarousel/PdpSharedSellerItemsCarousel';
import { PdpRespCondensedCircles } from '../PdpRespCondensedCircles/PdpRespCondensedCircles';
import { PdpRespDealerChatLoader } from '../PdpRespDealerChat/PdpRespDealerChatLoader';
import { SharedBillboardAd } from '../../shared/SharedBillboardAd/SharedBillboardAd';
import { youMayAlsoLike } from '../../shared/sharedMessages';

// helpers
import { getRecentlyViewedDisplayInfo } from '../helpers/recentlyViewedDisplayInfo';
import { trackPdpViewMoreCtaClick } from 'dibs-recent-history/src/recentHistoryTracking';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { useAppSelector } from '../hooks/useReactReduxHooks';
import {
    PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT,
    PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespCondensedContainer.scss';

export function PdpRespCondensedContainerComponent({
    currency,
    viewer,
    user,
    item,
    selectedSkuId,
    similarLandingItems,
}) {
    const ymalScrollRef = useRef(null);
    const { similarItemsGrid } = viewer;
    const { isClient } = useAppSelector(({ relayVariables }) => relayVariables.variables);
    const { isBannerAdsAbTestVariant, isBot } = useServerVarsContext();
    const { serviceId: itemId } = item || {};
    const recentHistoryItems = (user?.recentHistoryItems?.edges || []).map(({ node }) => node);
    const { showRecentlyViewed } = getRecentlyViewedDisplayInfo({
        isClient,
        itemId,
        recentHistoryItems,
    });

    usePdpRecentHistoryTracker({ item });

    return (
        <>
            <>
                <div className={styles.topBarWrapper}>
                    {isClient && (
                        <PdpRespSimilarItemsLanding
                            scrollToRef={ymalScrollRef}
                            item={item}
                            similarItems={similarLandingItems}
                            user={user}
                            landingItemSize={item?.isSold ? undefined : 'small'}
                            currency={currency}
                        />
                    )}
                </div>

                <PdpRespCondensedListedContent
                    currency={currency}
                    item={item}
                    user={user}
                    viewer={viewer}
                    selectedSkuId={selectedSkuId}
                />
            </>

            {/* More from Seller */}
            <PdpSharedSellerItemsCarousel
                viewer={viewer}
                item={item}
                user={user}
                currency={currency}
                classOverrides={{
                    container: classnames(
                        dibsCss.mtSmall,
                        dibsCss.borderT,
                        dibsCss.borderTSolid,
                        dibsCss.borderTDolphin,
                        dibsCss.ptMedium,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.itemsCenter
                    ),
                    title: classnames(dibsCss.sassyFontHeaderMedium, dibsCss.m0),
                    viewAllLink: classnames(dibsCss.sassyFontBody, dibsCss.mtXsmall),
                }}
            />

            {isBannerAdsAbTestVariant && (
                <SharedBillboardAd
                    placement={PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT}
                    className={dibsCss.myLarge}
                />
            )}
            {/* Pdp Dealer Chat */}
            {isClient && <PdpRespDealerChatLoader viewer={viewer} currency={currency} />}
            {/* Circles */}
            <PdpRespCondensedCircles item={item} />
            {/* You May Also Like */}
            <div ref={ymalScrollRef}>
                <PdpRespSimilarItemsGrid
                    gridItemWrapperClass={styles.similarItems}
                    relayVariables={{ isClient }}
                    viewer={viewer}
                    currency={currency}
                    user={user}
                    item={item}
                    header={youMayAlsoLike}
                    similarItems={similarItemsGrid?.edges?.map(({ node }) => node)}
                    favoritesLocation={locations.SIMILAR_ITEM_GRID}
                    dataTn="pdp-similar-recommendation-grid"
                    isDark={false}
                    showLightBox={false}
                    hideBar
                />
            </div>
            {isBannerAdsAbTestVariant && (
                <SharedBillboardAd
                    placement={PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT}
                    className={dibsCss.myLarge}
                />
            )}
            {isClient && showRecentlyViewed && (
                <RecentlyViewedItemsLazy
                    isBot={isBot}
                    authModalLoader={authModalLoader}
                    trackViewMoreClick={trackPdpViewMoreCtaClick}
                    currency={currency}
                    hasTopSeparator
                />
            )}
            <div className={styles.buyerIncentives}>
                <PdpSharedBuyerIncentivesModule />
            </div>
        </>
    );
}

PdpRespCondensedContainerComponent.propTypes = {
    currency: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,
    similarLandingItems: PropTypes.object,
};

export const PdpRespCondensedContainer = createFragmentContainer(
    PdpRespCondensedContainerComponent,
    {
        viewer: graphql`
            fragment PdpRespCondensedContainer_viewer on Viewer {
                ...PdpRespSimilarItemsGrid_viewer
                ...PdpRespCondensedListedContent_viewer
                ...PdpSharedSellerItemsCarousel_viewer @include(if: $fetchPdp)
                ...PdpRespDealerChatLoader_viewer @include(if: $isClient)
                similarItemsGrid: moreLikeThisItem(
                    first: 16
                    seller: EXCLUDE
                    itemId: $itemId
                    useML: true
                ) @include(if: $fetchPdp) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespCondensedContainer_user on User {
                serviceId
                ...PdpRespSimilarItemsGrid_user
                ...PdpRespSimilarItemsLanding_user
                ...PdpRespCondensedListedContent_user
                ...PdpSharedSellerItemsCarousel_user
                ...getRecentHistory_user @relay(mask: false)
            }
        `,
        item: graphql`
            fragment PdpRespCondensedContainer_item on Item {
                serviceId
                isSold
                ...usePdpRecentHistoryTracker_item
                ...PdpRespCondensedListedContent_item
                ...PdpRespSimilarItemsLanding_item
                ...PdpSharedSellerItemsCarousel_item
                ...PdpRespSimilarItemsGrid_item
                ...PdpRespCondensedCircles_item
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespCondensedContainer_similarLandingItems on moreLikeThisItemConnection {
                ...PdpRespSimilarItemsLanding_similarItems
            }
        `,
    }
);
